export default {
  /**
   * Returns a function, that, as long as it continues to be invoked, will not
   * be triggered. The function will be called after it stops being called for
   * N milliseconds. If `immediate` is passed, trigger the function on the
   * leading edge, instead of the trailing.
   * From: https://davidwalsh.name/javascript-debounce-function
   * @param {*} func
   * @param {*} wait
   * @param {*} immediate
   */
  debounce(func, wait = 100, immediate = false) {
    let timeout
    return (...args) => {
      const context = this
      const later = () => {
        timeout = null
        if (!immediate) func.apply(context, args)
      }
      const callNow = immediate && !timeout
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
      if (callNow) {
        func.apply(context, args)
      }
    }
  },
  /**
   * Decodes base64 into an unicode string
   * @param {string} base64
   * @returns string
   */
  base64ToUnicode(base64) {
    // jfc javascript does not play well with base64 unicode
    // https://stackoverflow.com/a/30106551
    return decodeURIComponent(atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''))
  },
  /**
   * Shuffles array in place.
   * @param {Array} a items An array containing the items.
   */
  shuffle(a) {
    let j
    let x
    let i
    for (i = a.length - 1; i > 0; i--) {
      j = Math.floor(Math.random() * (i + 1))
      x = a[i]
      a[i] = a[j]
      a[j] = x
    }
    return a
  },
  /**
   * Capitalize first letter of string
   * @param {String} input String to capitalize
   */
  capitalize(input) {
    return input.charAt(0).toUpperCase() + input.slice(1)
  },
  /**
   * Extract YouTube video ID from a URL.
   * Covers watch, embed, shorts, youtu.be and nocookie embed URLs.
   * @param {String} url
   * @returns {String|null}
   */
  extractYoutubeId(url) {
    // Primary regex covering most YouTube URL formats
    const ytRegex = /https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/gi
    const idPart = url.replace(ytRegex, '$1')
    if (!idPart.match(/^[a-zA-Z0-9_-]+$/)) {
      return null
    }
    return idPart
  },
  /**
   * Returns the OS
   * @returns {string}
   */
  getOS() {
    if (/android/i.test(navigator.userAgent)) {
      return 'android'
    }
    // newer versions of iPad will identify as `MacIntel` with multitouch instead of as iPad
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'ios'
    }
    return 'other'
  },
  /**
   * Returns true if the viewport is mobile
   * @returns {Boolean}
   */
  isMobileViewport() {
    if (typeof window === 'undefined') {
      return false
    }
    if (window.matchMedia) {
      return window.matchMedia('(max-width: 767px)').matches
    }
    return window.innerWidth <= 767
  },
  /**
   * This removes our "weirdness" from the language code and returns an ISO 639-1 one.
   * @param {string} language
   * @returns {string}
   */
  normalizeLanguage(language) {
    if (language === 'de_formal') {
      return 'de'
    }
    if (language === 'jp') {
      return 'ja'
    }
    return language
  },
  /**
   * Returns true if the device has touch capability (mobile or tablet)
   * Detects touch support including iPads and tablets
   * @returns {Boolean}
   */
  isTouchDevice() {
    // Check for touch points
    if (navigator.maxTouchPoints > 0) {
      return true
    }

    // Fallback for older browsers
    if ('ontouchstart' in window || (window.DocumentTouch && document instanceof window.DocumentTouch)) {
      return true
    }

    // Check media query for pointer type
    if (window.matchMedia) {
      return window.matchMedia('(pointer: coarse)').matches
    }

    return false
  },
  /**
   * Returns true if the device is a mobile/tablet touch device (not hybrid desktop)
   * Combines touch detection with mobile OS or viewport check to avoid misclassifying
   * hybrid desktops with touchscreens
   * @returns {Boolean}
   */
  isMobileTouchDevice() {
    // First check if device has touch capability
    if (!this.isTouchDevice()) {
      return false
    }

    // Check if it's a mobile OS (iOS or Android)
    const mobileOS = this.getOS()
    if (mobileOS === 'ios' || mobileOS === 'android') {
      return true
    }

    // Fallback: check viewport width for tablets/mobile devices
    // Use 768px as threshold (common tablet breakpoint)
    if (window.innerWidth < 768) {
      return true
    }

    return false
  },
}
